import styled, { css } from 'styled-components'
import { Typography } from '../typography'
import {
  AssigneePickerProps,
  Assignee,
  PickerStatus,
  AssigneePickerTriggerButtonProps,
} from './assignee-picker.types'
import { getColorForStatus } from './utils'
import { Flex, Popover } from '@mantine/core'
import { SearchInput } from '../search-input'
import { useDisclosure } from '@mantine/hooks'
import { Spinner } from '../spinner'
import { Tooltip } from '../tooltip'
import { theme } from '../../theme'
import { ButtonText } from '../button-text'

const TriggerButton = styled.button<{ $status: PickerStatus }>`
  ${({ theme, $status }) => css`
    display: flex;
    border-radius: ${theme.borderRadii.large};
    border: 1px solid ${theme.colors.gray90};
    gap: ${theme.sizes.xs};
    width: fit-content;
    text-wrap: nowrap;
    background: none;
    cursor: pointer;
    padding: 0;

    &:hover {
      background: ${theme.colors.gray70};
      border-color: ${theme.colors.gray70};
    }

    &:focus,
    &:active {
      border-color: ${theme.colors.primary400};
    }

    ${$status === 'overdue' &&
    `
      background-color: ${theme.colors.alert30};
      border-color: ${theme.colors.alert100};

      &:hover {
        background-color: ${theme.colors.alert100};
        border-color: ${theme.colors.alert100};
      }
    `}
    ${$status === 'completed' &&
    `
      background-color: ${theme.colors.success30};
      border-color: ${theme.colors.success100};

      &:hover {
        background-color: ${theme.colors.success100};
        border-color: ${theme.colors.success100};
      }
    `}
    ${$status === 'wont_do' &&
    `
      background: none;
      border-color: ${theme.colors.gray90};
      text-decoration: line-through;

      &:hover {
        background: none;
        border-color: ${theme.colors.gray90};
      }
    `}
    &:disabled {
      border: none;
      background: none;
    }
  `}
`

const AvatarPlaceholder = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sizes.s};
    height: ${theme.sizes.s};
    border-radius: 50%;
    border: 1px dashed ${theme.colors.gray100};

    > span {
      font-size: 0.675rem;
      text-transform: uppercase;
      font-weight: ${theme.fontWeights.semiBold};
      color: ${theme.colors.gray600};
      line-height: ${theme.sizes.l};
    }

    &:has(span) {
      border: none;
      background-color: ${theme.colors.gray90};
    }
  `}
`

const StyledPopover = styled(Popover.Dropdown)<{
  $hasSearch: boolean
  $hasBottomButton: boolean
}>`
  ${({ theme, $hasSearch, $hasBottomButton }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 240px;
    border-radius: ${theme.borderRadii.large};
    box-shadow: 0px 2px 16px 0px rgba(13, 13, 13, 0.08);
    gap: ${theme.sizes.xs};
    padding: ${!$hasSearch ? theme.sizes.xs : 0} 0
      ${$hasBottomButton ? 0 : theme.sizes.xs};
    background-color: ${theme.colors.white};
    border: none;
    max-height: 300px;
    overflow-y: auto;
  `}
`

export const StyledOption = styled.li`
  ${({ theme }) => css`
    list-style: none;
    padding: ${theme.sizes[2]} ${theme.sizes[4]};
    border-left: 0.125rem solid transparent;
    cursor: pointer;

    &.hover,
    &:hover {
      border-color: ${theme.colors.primary400};
      background-color: ${theme.colors.gray70};
    }
  `}
`

const LoaderWrapper = styled(Flex)`
  ${({ theme }) => css`
    align-items: center;
    padding: ${theme.sizes.xs} ${theme.sizes.s};
    gap: ${theme.sizes.xs};
  `}
`

const TruncatedText = styled(Typography)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
`

const SearchInputWrapper = styled(Flex)`
  ${({ theme }) => css`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${theme.colors.white};
    padding: ${theme.sizes.xs} ${theme.sizes.xs} 0;
  `}
`

const BottomButtonWrapper = styled.div`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.gray80};
    padding: ${theme.sizes.xs} ${theme.sizes[4]};
    background: ${theme.colors.white};
    position: sticky;
    bottom: 0;
    z-index: 1;
  `}
`

const AssigneePickerTriggerButton = (
  props: AssigneePickerTriggerButtonProps
) => (
  <TriggerButton
    onClick={props.onClick}
    disabled={props.disabled}
    $status={props.status}
    data-testid={props.triggerTestId}
  >
    <Tooltip content={props.tooltipContent}>
      <Flex gap={theme.sizes.xs} p={theme.sizes.xs}>
        <AvatarPlaceholder>
          {props.assignee && <span>{props.assignee.email.slice(0, 1)}</span>}
        </AvatarPlaceholder>
        <Typography
          variant="small"
          $color={getColorForStatus(props.status)}
          data-testid="status-label"
        >
          {props.statusLabel}
        </Typography>
      </Flex>
    </Tooltip>
  </TriggerButton>
)

export const AssigneePicker = (props: AssigneePickerProps) => {
  const [opened, { close, toggle }] = useDisclosure(false)

  if (props.readonly) {
    return <AssigneePickerTriggerButton {...props} disabled />
  }

  const {
    assigneeList,
    onAssigneeChange,
    search,
    isLoading,
    customLoadingMessage,
    noResultsMessage,
    status,
    bottomButtonText,
  } = props

  const isDisabled =
    status === 'completed' || status === 'wont_do' || props.disabled

  const handleAssigneeChange = (assignee: Assignee) => {
    onAssigneeChange?.(assignee)
    close()
  }

  return (
    <Popover
      position="bottom-start"
      opened={opened}
      onClose={close}
      onDismiss={close}
      disabled={isDisabled}
    >
      <Popover.Target>
        <div>
          <AssigneePickerTriggerButton {...props} onClick={toggle} />
        </div>
      </Popover.Target>

      <StyledPopover
        data-testid="assignee-picker-popover"
        $hasSearch={!!search}
        $hasBottomButton={!!bottomButtonText}
      >
        {search && (
          <SearchInputWrapper>
            <SearchInput
              placeholder={search.placeholder ?? 'Search by email'}
              value={search.value}
              handleClearValue={() => search.onChange('')}
              onChange={(e) => search.onChange(e.target.value)}
              data-testid="assignee-picker-search-input"
            />
          </SearchInputWrapper>
        )}

        {isLoading && (
          <LoaderWrapper>
            <Spinner size="s" color="primary" />
            <Typography
              variant="small"
              $color="primary400"
              data-testid="assignee-picker-loading-state"
            >
              {customLoadingMessage ?? 'Loading...'}
            </Typography>
          </LoaderWrapper>
        )}

        {!isLoading && assigneeList && assigneeList.length > 0 && (
          <Flex direction="column" component="ul" m={0} p={0}>
            {assigneeList?.map((assignee, index) => (
              <StyledOption
                key={assignee.id}
                onClick={() => handleAssigneeChange(assignee)}
                data-testid={`assignee-${index}`}
              >
                <TruncatedText variant="small" $color="text_primary">
                  {assignee.email}
                </TruncatedText>
              </StyledOption>
            ))}
          </Flex>
        )}

        {!isLoading && assigneeList?.length === 0 && (
          <Flex px={16} py={8}>
            <Typography variant="small" $color="text_primary">
              {noResultsMessage ?? "We couldn't find anyone"}
            </Typography>
          </Flex>
        )}
        {bottomButtonText && (
          <BottomButtonWrapper>
            <ButtonText
              onClick={bottomButtonText.onClick}
              disabled={bottomButtonText.disabled}
              variant={bottomButtonText.variant}
            >
              {bottomButtonText.label}
            </ButtonText>
          </BottomButtonWrapper>
        )}
      </StyledPopover>
    </Popover>
  )
}
